import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { sendAssignmentEmail } from '../../utils/api';

export default function ResendPendingModal({ open, onClose, items, teamName, senderEmail, canSend, onSent, showToast }) {
  const [sending, setSending] = useState(false);
  // Per-person selection — leads choose who gets a reminder (default: everyone).
  const [checked, setChecked] = useState(() => new Set());

  const byPerson = {};
  for (const item of items) {
    if (!byPerson[item.personId]) {
      byPerson[item.personId] = { personId: item.personId, name: item.personName, email: item.personEmail, sentAt: item.sentAt, positions: [], items: [] };
    }
    byPerson[item.personId].positions.push(`${item.blockTitle}: ${item.positionLabel}`);
    byPerson[item.personId].items.push(item);
  }
  const people = Object.values(byPerson);

  useEffect(() => {
    if (open) setChecked(new Set(items.map(i => i.personId)));
  }, [open, items]);

  const toggleChecked = (personId) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(personId)) next.delete(personId); else next.add(personId);
    return next;
  });
  const selectedPeople = people.filter(p => checked.has(p.personId));
  const allChecked = people.length > 0 && selectedPeople.length === people.length;

  function daysAgo(iso) {
    if (!iso) return null;
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  }

  async function handleSend() {
    if (!canSend) { showToast('Only @thebelonging.co staff can send emails.'); return; }
    if (selectedPeople.length === 0) { showToast('Select at least one person to remind.'); return; }
    setSending(true);

    // One reminder per SELECTED person (not per slot), linking to their page.
    const results = await Promise.allSettled(
      selectedPeople
        .filter(p => p.email)
        .map(p => {
          const blockName = p.items.length === 1 ? p.items[0].blockTitle : `${p.items.length} serving positions`;
          return sendAssignmentEmail(p.email, p.name, p.personId, blockName, teamName, senderEmail)
            .catch(err => { console.error('Resend failed for', p.name, err); return null; });
        })
    );

    const sent = results.filter(r => r.status === 'fulfilled' && r.value !== null).length;
    showToast(`${sent} reminder${sent !== 1 ? 's' : ''} sent.`);
    setSending(false);
    if (onSent) onSent();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Resend Pending Requests"
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSend} disabled={sending || items.length === 0}>
            {sending ? 'Sending…' : `Send ${selectedPeople.length} Reminder${selectedPeople.length !== 1 ? 's' : ''}`}
          </button>
        </>
      }
    >
      <div className="notice-bar" style={{ marginBottom: 20 }}>
        These people have a schedule request pending but haven't responded yet. A reminder email will be sent from <strong>your account</strong> ({senderEmail || 'your email'}).
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          Pending — {selectedPeople.length} of {people.length} selected
        </span>
        {people.length > 0 && (
          <button
            className="btn btn-xs"
            onClick={() => setChecked(allChecked ? new Set() : new Set(items.map(i => i.personId)))}
          >
            {allChecked ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}>
        {people.map((p, i) => (
          <label
            key={p.email + i}
            style={{ padding: '11px 16px', borderBottom: i < people.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', background: checked.has(p.personId) ? 'var(--white)' : 'var(--off)' }}
          >
            <input
              type="checkbox"
              checked={checked.has(p.personId)}
              onChange={() => toggleChecked(p.personId)}
              style={{ accentColor: 'var(--black)', flexShrink: 0 }}
            />
            <div style={{ flex: 1, opacity: checked.has(p.personId) ? 1 : 0.55 }}>
              <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>{p.name}</div>
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>
                {daysAgo(p.sentAt) ? `Sent ${daysAgo(p.sentAt)} · ` : ''}{p.positions.join(' · ')}
              </div>
            </div>
            <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', flexShrink: 0, marginLeft: 12 }}>{p.email}</span>
          </label>
        ))}
        {items.length === 0 && (
          <div style={{ padding: '16px', textAlign: 'center', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
            No pending requests.
          </div>
        )}
      </div>

      {!canSend && (
        <div style={{ marginTop: 12, background: '#fffbf0', border: '1px solid #d4920a', borderRadius: 'var(--radius)', padding: '11px 14px', fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: '#7a4f0a' }}>
          Only <strong>@thebelonging.co</strong> accounts can send emails.
        </div>
      )}
    </Modal>
  );
}
